import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Mail, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

export const EmailVerificationBanner = () => {
  const { user, isAuthenticated, resendVerification } = useAuth();
  const [sending, setSending] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Show only for real users whose email is not yet verified.
  // Note: intentionally does NOT respect `isVerified` — that flag also flips
  // true for role-verified admins/artists/agents, which would hide the banner
  // for a fresh artist signup whose email is still unverified. Email
  // verification is a distinct concept from role authorisation.
  const emailVerified = user?.email_verified === true;
  const inTestMode = !!user?.test_mode?.active;
  if (!isAuthenticated || emailVerified || inTestMode || dismissed) return null;

  const handleResend = async () => {
    setSending(true);
    try {
      await resendVerification();
      toast.success('Verification email sent! Check your inbox.');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send verification email';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="bg-amber-900/30 border-b border-amber-700/40 px-4 py-2.5 flex items-center justify-between gap-3"
      data-testid="email-verification-banner"
    >
      <div className="flex items-center gap-2 text-sm text-amber-200">
        <Mail className="w-4 h-4 shrink-0" />
        <span>
          Please verify your email (<strong>{user?.email}</strong>) to order dubplates and message artists.
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          size="sm"
          onClick={handleResend}
          disabled={sending}
          className="bg-amber-600 hover:bg-amber-500 text-white text-xs h-7 px-3"
          data-testid="resend-verification-btn"
        >
          {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
          Resend Email
        </Button>
        <button
          onClick={() => setDismissed(true)}
          className="text-amber-400 hover:text-white transition-colors"
          data-testid="dismiss-verification-banner"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
